import Cookies from 'js-cookie'

import BuildConfig from '$extensionSrc/buildConfig'
import { RedditSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { sendAlertNotification } from '$extensionSrc/utils/communication'
import {
  PLATFORM,
  StageType,
  RedditRecommendationKey,
} from '$extensionSrc/utils/enums'
import { _debug } from '$extensionSrc/utils/logging'

import { NO_TIMEOUT } from '../../consts/stageMachineConsts'
import { StageDef } from '../automationMachineInterfaces'
import {
  getStoredMachineOverlayStatus,
  setStoredMachineOverlayStatus,
} from '../automationStageMachine'
import { wait } from '../interruptiblePromises'
import {
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import {
  deleteContent,
  fetchMeJson,
  getContentIdIterator,
  getJitteredValue,
  pluralize,
  redactContent,
  RedditContentType,
  redditFetch,
} from './contentUtils'
import * as redditUrls from './redditUrls'
import { getInitialValueBySettingKey } from '../utils'

type RedditCommentDeleteState = {
  count: number // total count of comments deleted all-time
  message: string // Used for the RecommendationCard footer text
  result?: {
    // Used for the RecommendationCard banner
    title: string
    detail?: string
  }
  username?: string
}

function buildResultObject(
  totalDeleted: number,
  nDeleted: number,
  username: string,
): RedditCommentDeleteState {
  let result: { title: string; detail?: string } | undefined
  if (nDeleted === 0) {
    result = {
      title: 'No comments deleted',
      detail: 'We were not able to find any comments to delete on your profile',
    }
  } else {
    result = {
      title: `You deleted ${pluralize(nDeleted, RedditContentType.Comment)}.`,
    }
  }

  return {
    count: totalDeleted,
    message: `Deleted ${pluralize(totalDeleted, RedditContentType.Comment)} (all-time).`,
    result,
    username,
  }
}

async function updateMachineOverlayStatus(nDeleted: number) {
  const currentStatus = await getStoredMachineOverlayStatus(PLATFORM.REDDIT)
  return setStoredMachineOverlayStatus(PLATFORM.REDDIT, {
    ...currentStatus,
    nDeleted,
  })
}

async function redactComment(commentId: string) {
  const csrfToken = Cookies.get('csrf_token')

  if (!csrfToken) {
    _debug(
      'CSRF token missing for GQL comment edit, falling back to legacy API',
      'reddit.redactComment',
      'w',
    )
    return redactContent(commentId)
  }

  const gqlBody = {
    operation: 'UpdateComment',
    variables: {
      input: {
        commentId,
        content: {
          richText:
            '{"document":[{"e":"par","c":[{"e":"text","t":"[deleted]","f":[[0,0,9]]}]}]}',
        },
      },
    },
    csrf_token: csrfToken,
  }

  try {
    return await redditFetch(redditUrls.apiGqlUrl, {
      body: JSON.stringify(gqlBody),
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
    })
  } catch (error) {
    _debug(
      `GQL comment edit failed for ${commentId}: ${error.message}`,
      'reddit.redactComment',
      'e',
    )
    return redactContent(commentId)
  }
}

async function deleteComment(commentId: string) {
  const csrfToken = Cookies.get('csrf_token')

  if (!csrfToken) {
    _debug(
      'CSRF token missing for GQL comment delete, falling back to legacy API',
      'reddit.deleteComment',
      'w',
    )
    return deleteContent(commentId)
  }

  const gqlBody = {
    operation: 'DeleteComment',
    variables: {
      input: {
        commentId,
      },
    },
    csrf_token: csrfToken,
  }

  try {
    return await redditFetch(redditUrls.apiGqlUrl, {
      body: JSON.stringify(gqlBody),
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
    })
  } catch (error) {
    _debug(
      `GQL comment delete failed for ${commentId}: ${error.message}`,
      'reddit.deleteComment',
      'e',
    )
    return deleteContent(commentId)
  }
}

const getCurrentValueFn = () => () => ''

const getIsFulfilledFn = () => () => true

const StageKey = {
  UPDATE_LOAD_OVERVIEW_PAGE: 'UPDATE_LOAD_OVERVIEW_PAGE',
  UPDATE_REDACT_DELETE_COMMENTS: 'UPDATE_REDACT_DELETE_COMMENTS',
  UPDATE_REFRESH_FINAL: 'UPDATE_REFRESH_FINAL',
}

const gatherStageDefs: StageDef[] = []

const getCantUndoFn = () => () => true

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = [
  // Navigate to the user's overview page, load the user's username and clear run stats
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_LOAD_OVERVIEW_PAGE,
    status: {
      title: 'Getting Started...',
      body: 'Gathering account information.',
    },
    url: redditUrls.profileUrl,
    urlPattern: 'https://www.reddit.com/user/.+',
    action: async (_machineContext, userContext) => {
      const { initialValues: storedInitialValues, updateStoredInitialValues } =
        userContext

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        RedditSettingKey.DELETE_COMMENTS,
      )

      try {
        // Throws error if username is not found
        const { username } = await fetchMeJson()

        const updatedInitialValues = {
          [RedditSettingKey.DELETE_COMMENTS]: {
            ...initialValues,
            stats: {
              latestRunDeleted: 0,
            },
            username,
          },
        }

        await updateStoredInitialValues(
          updatedInitialValues,
          RedditRecommendationKey.REDDIT_DELETE_COMMENTS,
        )
      } catch (error) {
        _debug(
          `Failed to gather user info: ${error.message}`,
          'reddit.loadOverviewStage',
          'e',
        )
        sendAlertNotification(
          PLATFORM.REDDIT,
          'Reddit: Error',
          'Unable to get account information',
        )
        throw error
      }
    },
  },
  // Redact and delete comments
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_REDACT_DELETE_COMMENTS,
    status: {
      title: `${BuildConfig.TENANT_NAME} is deleting your comments...`,
      body: 'Please keep this tab open.',
    },
    timeout: NO_TIMEOUT,
    action: async (_machineContext, userContext, additionalMachineData) => {
      const {
        incrementTotalCount,
        initialValues: storedInitialValues,
        isDry,
        updateStoredInitialValues,
      } = userContext
      const { resumeData } = additionalMachineData
      const { username } = await fetchMeJson()

      try {
        const commentIdIter = getContentIdIterator(
          username,
          RedditContentType.Comment,
        )

        const initialValues = getInitialValueBySettingKey(
          storedInitialValues,
          RedditSettingKey.DELETE_COMMENTS,
        )

        let totalDeleted = initialValues?.count || 0
        let currentDeleted = resumeData?.data?.deletedComments || 0

        for await (const commentId of commentIdIter) {
          if (isDry) {
            await wait(getJitteredValue(500, 250))
          } else {
            await redactComment(commentId)
            await wait(getJitteredValue(500, 250))
            await deleteComment(commentId)
            await wait(getJitteredValue(500, 250))
          }

          totalDeleted += 1
          currentDeleted += 1

          await incrementTotalCount(
            RedditRecommendationKey.REDDIT_DELETE_COMMENTS,
          )

          await updateMachineOverlayStatus(currentDeleted)
          await resumeData.set({
            ...resumeData.data,
            deletedComments: currentDeleted,
          })

          await updateStoredInitialValues(
            {
              [RedditSettingKey.DELETE_COMMENTS]: buildResultObject(
                totalDeleted,
                currentDeleted,
                username,
              ),
            },
            RedditRecommendationKey.REDDIT_DELETE_COMMENTS,
          )

          await userContext.updateStatisticsLastCount(
            RedditRecommendationKey.REDDIT_DELETE_COMMENTS,
            currentDeleted,
          )
        }
      } catch (error) {
        _debug(
          `Failed to complete comment deletion: ${error.message}`,
          'reddit.deleteCommentsStage',
          'e',
        )
        sendAlertNotification(
          PLATFORM.REDDIT,
          'Reddit: Delete Comments Error',
          'Failed to complete comment deletion',
        )
        throw error
      }
    },
  },
  // Refresh the page to show the content has been deleted
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_REFRESH_FINAL,
    status: {
      title: 'Wrapping up...',
      body: 'Reloading the page.',
    },
    url: redditUrls.profileUrl,
    urlPattern: 'https://www.reddit.com/user/.+',
    action: async (_machineContext, _userContext, additionalMachineData) => {
      const { resumeData } = await additionalMachineData
      await resumeData.clear()
      window.location.reload()
    },
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
